import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './ViewVendorAppointment.module.css';
import vendorAppointmentsStyles from '../VendorAppointments.module.css';
import CloseIcon from '../../../svgIcons/CloseIcon';
import parsePhoneNumberFromString from 'libphonenumber-js';
import moment from 'moment';
import InfoIcon from '../../../svgIcons/InfoIcon';
import { deleteVendorAppointment } from '../../../../redux/vendor/actions/vendor.action';
import { AppDispatch } from '../../../../redux/rootReducer';
import CustModal from '../../../common/custmodal/CustModal';
import DeleteIcon from '../../../svgIcons/DeleteIcon';
import Loader from '../../../common/loader/Loader';
import OutlinedCloseIcon from '../../../svgIcons/OtlinedCloseIcon';

interface ViewVendorAppointmentProps {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<React.SetStateAction<string>>;
    setToastType: React.Dispatch<React.SetStateAction<string>>;
    vendorAppointmentList: {
        locationId: string;
        vendorName: string;
        vendorEmail: string;
        startDate: string;
        endDate: string;
        startTime: string;
        endTime: string;
        locationName: string;
        vendorPhone: string;
        locker: string;
        id: string;
        vendorSpecialNote?: string;
        allowEntireTimeBlock: boolean;
        allowStratEndTimeEachDay: boolean;
    };
    onDeleteSuccess?: () => void; // Callback to refetch data after successful delete
}

const ViewVendorAppointment: React.FC<ViewVendorAppointmentProps> = ({
    setOpen,
    setShowToast,
    setToastMessage,
    setToastType,
    vendorAppointmentList,
    onDeleteSuccess,
}) => {
    const dispatch: AppDispatch = useDispatch();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeleteClick = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = () => {
        if (vendorAppointmentList?.id) {
            setIsDeleting(true);
            setShowConfirmModal(false);

            dispatch(
                deleteVendorAppointment(
                    vendorAppointmentList.id,
                    (message: string) => {
                        // Success callback
                        setIsDeleting(false);
                        setToastType('success');
                        setShowToast(true);
                        setToastMessage(message);
                        setOpen(false); // Close the modal
                        onDeleteSuccess?.(); // Refetch data
                    },
                    (message: string) => {
                        // Error callback
                        setIsDeleting(false);
                        setToastType('error');
                        setShowToast(true);
                        setToastMessage(message);
                    },
                ),
            );
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmModal(false);
    };

    function formatPhone(phone?: string): string {
        if (!phone) return '';
        const parsed = parsePhoneNumberFromString(phone);
        return parsed ? parsed.formatInternational() : phone;
    }

    // Confirmation Modal Component
    const ConfirmationModal = () => (
        <div className={vendorAppointmentsStyles.vendorAppointmentsActionModal}>
            <h2 className={vendorAppointmentsStyles.vendorAppointmentsTitle}>Are you sure you want to delete?</h2>
            <p className={vendorAppointmentsStyles.vendorAppointmentsDescription}>Vendor appointment cannot be recovered once deleted.</p>
            <div className={vendorAppointmentsStyles.vendorAppointmentsActions}>
                <button
                    className={vendorAppointmentsStyles.vendorAppointmentsCancelButton}
                    onClick={handleCancelDelete}
                    disabled={isDeleting}>
                    Cancel
                </button>
                <button
                    className={vendorAppointmentsStyles.vendorAppointmentsConfirmButton}
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}>
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
            </div>
        </div>
    );

    return (
        <div className={styles.modalContainer}>
            {isDeleting && <Loader />}
            {/* Modal Header */}
            <div className={styles.modalHeader}>
                <div className={styles.headerContent}>
                    <div className={styles.vendorInfo}>
                        <div className={styles.vendorAvatar}>
                            <div className={styles.avatarInitial}>{vendorAppointmentList.vendorName.charAt(0).toUpperCase()}</div>
                        </div>
                        <div className={styles.vendorDetails}>
                            <h2 className={styles.vendorName}>{vendorAppointmentList.vendorName}</h2>
                            <p className={styles.appointmentId}>Property: {vendorAppointmentList.locationName}</p>
                        </div>
                    </div>
                </div>
                <button className={styles.closeButton} onClick={handleClose} aria-label="Close modal">
                    <OutlinedCloseIcon fill="#fff" />
                </button>
            </div>

            {/* Modal Body */}
            <div className={styles.modalBody}>
                <div className={styles.twoColumnLayout}>
                    {/* Email */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Email</label>
                            <span className={styles.infoValue}>{vendorAppointmentList.vendorEmail}</span>
                        </div>
                    </div>

                    {/* Phone */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Phone</label>
                            <span className={styles.infoValue}>{formatPhone(`+${vendorAppointmentList.vendorPhone}`)}</span>
                        </div>
                    </div>

                    {/* Start Date */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Start Date</label>
                            <span className={styles.infoValue}>
                                {moment(vendorAppointmentList?.startDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}
                            </span>
                        </div>
                    </div>

                    {/* Start Time */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Start Time</label>
                            <span className={styles.infoValue}>
                                {vendorAppointmentList?.startTime}
                            </span>
                        </div>
                    </div>

                    {/* End Date */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>End Date</label>
                            <span className={styles.infoValue}>
                                {moment(vendorAppointmentList?.endDate, 'DD-MM-YYYY').format('YYYY-MM-DD')}
                            </span>
                        </div>
                    </div>

                    {/* End Time */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>End Time</label>
                            <span className={styles.infoValue}>
                                {vendorAppointmentList?.endTime}
                            </span>
                        </div>
                    </div>

                    {/* Locker Assignment */}
                    <div className={styles.infoItem}>
                        <div className={styles.infoContent}>
                            <label className={styles.infoLabel}>Locker</label>
                            <span className={styles.infoValue}>{vendorAppointmentList.locker}</span>
                        </div>
                    </div>

                    {/* Notes */}
                    {vendorAppointmentList?.vendorSpecialNote && (
                        <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                            <div className={styles.infoContent}>
                                <label className={styles.infoLabel}>Notes</label>
                                <span className={styles.infoValue}>{vendorAppointmentList?.vendorSpecialNote}</span>
                            </div>
                        </div>
                    )}

                    <div className={`${styles.infoItem} ${styles.fullWidth} ${styles.appointmentDetails}`}>
                        <div className={styles.infoContent}>
                            <div className={styles.infoIcon}>
                                <InfoIcon width={12} height={12} fillColor="#51ab86" />
                            </div>

                            <span className={styles.appointmentDetailsValue}>
                                {vendorAppointmentList.allowEntireTimeBlock
                                    ? 'This appointment allows continuous access for the entire time block without daily limits.'
                                    : 'This appointment has daily time slots, available only during the set start and end times each day'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Footer */}
            <div className={styles.modalFooter}>
                <button className={styles.deleteModalButton} onClick={handleDeleteClick} disabled={isDeleting}>
                    <DeleteIcon size={18} fill="#dc3545" />
                    <span>Delete Appointment</span>
                </button>
                <button className={styles.closeModalButton} onClick={handleClose} disabled={isDeleting}>
                    Close
                </button>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <CustModal
                    open={showConfirmModal}
                    setOpen={setShowConfirmModal}
                    bodyData={<ConfirmationModal />}
                    onCloseModal={handleCancelDelete}
                />
            )}
        </div>
    );
};

export default ViewVendorAppointment;
